import React, { Fragment, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { Divider, Grid, Box, makeStyles, IconButton, Tooltip } from '@material-ui/core';
import { GetApp as DownloadIcon } from '@material-ui/icons';
import { useRouteMatch } from 'react-router';

import { TitleContainer, ContentContainer, FilterYearComparison, Chip } from 'src/components';
import { EcosystemInventoryTable, EcosystemTrendsChart, SpeciesTrendsChart } from 'src/scenes/Project/components';
import { SpeciesInventoryTable } from 'src/scenes/Project/components/SpeciesInventoryTable';
import { AnalyticCard } from 'src/scenes/Project/components';
import { generateEcosystemTableCSV } from 'src/scenes/Project/components/ecosystemUtils';
import { useProjectApi } from 'src/services';
import { useFetchStatus } from 'src/hooks';
import { LoadingBox } from 'src/scenes/Project/scenes/Dashboard/components';


const useStyles = makeStyles(theme => ({
  comparisonYearGroup: {
    border: `1px solid ${theme.palette.common.gray}`,
    borderRadius: theme.shape.borderRadius,
  },
  group: {
    padding: '4px',
    borderRadius: theme.shape.borderRadius,
    borderWidth: 1,
    borderStyle: 'solid',
    borderColor: theme.palette.common.gray,
  },
  downloadButton: {
    width: 34,
    height: 34,
    border: `1px solid ${theme.palette.common.gray}`,
    borderRadius: theme.shape.borderRadius,
  },
}));

const viewModeOptions = [
  { label: 'Total', value: 'total' },
  { label: 'Formación', value: 'formation' },
  { label: 'Ecosistemas', value: 'vegetation-floor' },
  { label: 'Uso de suelo', value: 'land-use' },
];

const chartViewModeOptions = [
  { label: 'Formación', value: 'formation' },
  { label: 'Ecosistemas', value: 'vegetation-floor' },
  { label: 'Uso de suelo', value: 'land-use' },
];

const kingdomFilterOptions = [
  { label: 'Total', value: 'total' },
  { label: 'Flora', value: 'flora' },
  { label: 'Fauna', value: 'fauna' },
];

const areaTypeOptions = [
  { label: 'Área', value: 'area' },
  { label: 'Ajustada', value: 'adjustedArea' },
];

const availableYears = [ 2020, 2021, 2022, 2023, 2024 ];
const defaultYears = [ 2020, 2024 ];

const PhysicalAccountingContainer = ({ project }) => {
  const classes = useStyles();
  const match = useRouteMatch();
  const projectApi = useProjectApi();
  const [ speciesData, setSpeciesData ] = useState([]);

  const [ comparisonYears, setComparisonYears ] = useState(defaultYears);
  const [ speciesComparisonYears, setSpeciesComparisonYears ] = useState(defaultYears);
  const [ viewMode, setViewMode ] = useState('total');
  const [ chartViewMode, setChartViewMode ] = useState('formation');
  const [ areaType, setAreaType ] = useState('adjustedArea');
  const [ kingdomFilter, setKingdomFilter ] = useState('total');
  const [ ecosystems, setEcosystems ] = useState();

  let { projectId, siteId } = match.params;
  const isSite = Boolean(siteId);
  projectId = isSite ? siteId : projectId;

  const handleUpdateYear = newYears => {
    setComparisonYears(newYears);
  };

  const handleViewModeChange = newViewMode => {
    setViewMode(newViewMode);
  };

  const handleChartViewModeChange = newViewMode => {
    setChartViewMode(newViewMode);
  };

  const handleAreaTypeChange = newAreaType => {
    setAreaType(newAreaType);
  };

  const handleSpeciesYearUpdate = newYears => {
    setSpeciesComparisonYears(newYears);
  };

  const handleKingdomFilterChange = newKingdomFilter => {
    setKingdomFilter(newKingdomFilter);
  };

  const {
    handleConfirm: fetchEcosystems,
    isSuccess: isSuccessEcosystems,
    isLoading: isLoadingEcosystems,
  } = useFetchStatus({
    confirmFn: async () => {
      const ecosystems = await projectApi.getEcosystems({ projectId });
      if (ecosystems?.availableYears?.length) {
        const availableYears = ecosystems.availableYears;
        const firstYear = availableYears[0];
        const secondYear = availableYears.length > 1 ? ecosystems.availableYears[1] : secondYear;
        setComparisonYears([ firstYear, secondYear ]);
      }
      setEcosystems(ecosystems);
    },
  });

  useEffect(() => {
    fetchEcosystems();
  }, []); //eslint-disable-line

  const {
    handleConfirm: fetchSpeciesData,
    isSuccess: isSuccessSpeciesData,
    isLoading: isLoadingSpeciesData,
  } = useFetchStatus({
    confirmFn: async () => {
      const stats = await projectApi.getProjectOtherStats({ projectId });
      setSpeciesData(stats.physicalAccountingSpecies || []);
    },
  });

  useEffect(() => {
    fetchSpeciesData();
  }, []); //eslint-disable-line

  const handleDownloadEcosystemTable = () => {
    const success = generateEcosystemTableCSV(ecosystems, comparisonYears, viewMode, areaType);
    if (!success) {
      // Aquí podrías mostrar una notificación de error si tienes un sistema de notificaciones
      console.error('Error al generar la descarga CSV');
    }
  };

  const hasEcosystems = ecosystems?.ecosystems?.length;
  const hasSpecies = speciesData?.length;

  return (
    <Grid container>
      <TitleContainer
        maxWidth="xl"
        breadcrumbs={[ 'projects', 'project', { name: 'Contabilidad física' } ]}
        project={project}
      >
        Contabilidad física
      </TitleContainer>
      <ContentContainer maxWidth="xl">
        <Grid container spacing={2}>
          {(hasEcosystems || isLoadingEcosystems) &&
            <Grid item xs={12}>
              <AnalyticCard
                title="Activos por ecosistema"
                ActionComponent={() =>
                  hasEcosystems ?
                    <Tooltip title="Agrupar datos del gráfico" placement="top">
                      <Box display="flex" className={classes.group} p={0.5}>
                        {chartViewModeOptions.map((option, index) => (
                          <Fragment key={option.value}>
                            <Chip
                              color={chartViewMode === option.value ? 'secondary.main' : 'gray'}
                              label={option.label}
                              size="small"
                              onClick={() => handleChartViewModeChange(option.value)}
                              width={120}
                            />
                            {index < chartViewModeOptions.length - 1 && <Box mx={0.5} />}
                          </Fragment>
                        ))}
                      </Box>
                    </Tooltip> : null
                }
              >
                <Divider />
                <br />
                {isLoadingEcosystems && <LoadingBox label="Activos por ecosistema" />}
                {isSuccessEcosystems && hasEcosystems &&
                <EcosystemTrendsChart
                  ecosystems={ecosystems}
                  viewMode={chartViewMode}/>
                }
              </AnalyticCard>
            </Grid>
          }
          {(hasEcosystems || isLoadingEcosystems) &&
            <Grid item xs={12}>
              <AnalyticCard
                title="Inventario de activos por ecosistema"
                ActionComponent={() =>
                  hasEcosystems ?
                    <Box display="flex" alignItems="center" gap={2}>
                      <Tooltip title="Tipo de área" placement="top">
                        <Box display="flex" className={classes.group} p={0.5}>
                          {areaTypeOptions.map((option, index) => (
                            <Fragment key={option.value}>
                              <Chip
                                color={areaType === option.value ? 'secondary.main' : 'gray'}
                                label={option.label}
                                size="small"
                                onClick={() => handleAreaTypeChange(option.value)}
                                width={80}
                              />
                              {index < areaTypeOptions.length - 1 && <Box mx={0.5} />}
                            </Fragment>
                          ))}
                        </Box>
                      </Tooltip>
                      <Box px={0.5} />
                      <Tooltip title="Agrupar datos" placement="top">
                        <Box display="flex" className={classes.group} p={0.5}>
                          {viewModeOptions.map((option, index) => (
                            <Fragment key={option.value}>
                              <Chip
                                color={viewMode === option.value ? 'secondary.main' : 'gray'}
                                label={option.label}
                                size="small"
                                onClick={() => handleViewModeChange(option.value)}
                                width={120}
                              />
                              {index < viewModeOptions.length - 1 && <Box mx={0.5} />}
                            </Fragment>
                          ))}
                        </Box>
                      </Tooltip>
                      <Box px={0.5} />
                      <Tooltip title="Elegir años a comparar" placement="top">
                        <Box className={classes.comparisonYearGroup} height={34}>
                          {isSuccessEcosystems && ecosystems && <FilterYearComparison
                            id="ecosystem-year-comparison"
                            label="Rango de años"
                            prevValue={comparisonYears}
                            options={ecosystems.availableYears}
                            onChange={handleUpdateYear}
                          />}
                        </Box>
                      </Tooltip>
                      <Box px={0.5} />
                      <Tooltip title="Exportar tabla" placement="top">
                        <IconButton
                          className={classes.downloadButton}
                          onClick={handleDownloadEcosystemTable}
                          disabled={isLoadingEcosystems}
                        >
                          <DownloadIcon />
                        </IconButton>
                      </Tooltip>
                    </Box> : null
                }
              >
                <Divider />
                <br />
                {isLoadingEcosystems && <LoadingBox label="Inventario de activos por ecosistema" />}
                {isSuccessEcosystems && hasEcosystems &&
                  <EcosystemInventoryTable
                    ecosystems={ecosystems}
                    comparisonYears={comparisonYears}
                    viewMode={viewMode}
                    areaType={areaType}
                    visibleColumns={[
                      'ecosystem',
                      'vegetationFloor',
                      'landUse',
                      `area${comparisonYears[0]}`,
                      `condition${comparisonYears[0]}`,
                      `adjustedArea${comparisonYears[0]}`,
                      `area${comparisonYears[1]}`,
                      `condition${comparisonYears[1]}`,
                      `adjustedArea${comparisonYears[1]}`,
                      'variation',
                      'performance',
                    ]}
                  />
                }
              </AnalyticCard>
            </Grid>
          }
          {(hasSpecies || isLoadingSpeciesData) &&
            <>
              <Grid item xs={12}>
                <AnalyticCard
                  title="Inventario de activos por especies"
                  ActionComponent={() =>
                    <Box display="flex" alignItems="center" gap={2}>
                      <Tooltip title="Filtrar por reino" placement="top">
                        <Box display="flex" className={classes.group} p={0.5}>
                          {kingdomFilterOptions.map((option, index) => (
                            <Fragment key={option.value}>
                              <Chip
                                color={kingdomFilter === option.value ? 'secondary.main' : 'gray'}
                                label={option.label}
                                size="small"
                                onClick={() => handleKingdomFilterChange(option.value)}
                                width={80}
                              />
                              {index < kingdomFilterOptions.length - 1 && <Box mx={0.5} />}
                            </Fragment>
                          ))}
                        </Box>
                      </Tooltip>
                      <Box px={0.5} />
                      <Tooltip title="Elegir años a comparar" placement="top">
                        <Box className={classes.comparisonYearGroup} height={34}>
                          <FilterYearComparison
                            id="species-year-comparison"
                            label="Rango de años"
                            prevValue={speciesComparisonYears}
                            options={availableYears}
                            onChange={handleSpeciesYearUpdate}
                          />
                        </Box>
                      </Tooltip>
                    </Box>
                  }
                >
                  <Divider />
                  <br />
                  {isLoadingSpeciesData && <LoadingBox label="Inventario de especies" />}
                  {isSuccessSpeciesData &&
                    <SpeciesInventoryTable
                      comparisonYears={speciesComparisonYears}
                      speciesData={speciesData}
                      kingdomFilter={kingdomFilter}
                    />
                  }
                </AnalyticCard>
              </Grid>
              <Grid item xs={12}>
                <AnalyticCard
                  title="Activos por especies"
                >
                  <Divider />
                  <br />
                  {isLoadingSpeciesData && <LoadingBox label="Especies" />}
                  {isSuccessSpeciesData &&
                    <SpeciesTrendsChart speciesData={speciesData} />
                  }
                </AnalyticCard>
              </Grid>
            </>
          }
        </Grid>
      </ContentContainer>
    </Grid>
  );
};

PhysicalAccountingContainer.propTypes = {
  project: PropTypes.shape({
    siteName: PropTypes.string,
    siteId: PropTypes.number,
    name: PropTypes.string,
    id: PropTypes.number,
  }),
};


export {
  PhysicalAccountingContainer,
};
